import { useRouter } from 'next/router';
import { BASE_PATHS, template } from '@belong/common';
import type { State } from '@belong/common';
import { ButtonBase, Image, Text } from '@belong/ui';
import { clsx } from 'clsx';
import { CoverageRegionModel } from 'src/api/models';
import { useRegions } from 'src/features/app';
import { useStore } from 'src/store';
import { pickRegionSelector, somewhereElse } from 'src/store/region';
import type { PickedRegion } from 'src/store/region';
import { RegionCard } from './region-card';
import type { From } from './region-select';
import styles from './state-card.module.css';
import {
  REGION_SELECT_STRINGS,
  REGION_SELECT_STRINGS as STRINGS,
} from './strings';

type StateCardProps = {
  code: State['code'];
  name: string;
  status: CoverageRegionModel['status'];
  src: string;
  hoverSrc: string;
  selected?: boolean;
  from?: From;
  onDismiss: () => void;
  onClick: () => void;
  search: string;
};

export function RegionList({
  withDescription = true,
  from,
  code,
  name,
  search,
  onDismiss,
}: {
  withDescription?: boolean;
} & Pick<StateCardProps, 'onDismiss' | 'from' | 'code' | 'name'> &
  Partial<Pick<StateCardProps, 'search'>>) {
  const router = useRouter();
  const { regionsByState } = useRegions();

  const pickRegion = useStore(pickRegionSelector);

  if (!regionsByState[code]) {
    return null;
  }

  const otherRegion: PickedRegion = {
    id: -1,
    region: REGION_SELECT_STRINGS['other_cities'],
    status: 'Other',
  };

  const regionList = [
    ...regionsByState[code],
    ...(from === 'homeowners' ? [otherRegion] : []),
  ];

  function handleRegionClick(region: PickedRegion) {
    return () => {
      onDismiss();

      if (region.status !== 'Other') {
        if (from === 'homeowners') {
          router.push(`${BASE_PATHS.HOMEOWNER}/${region.nameKebabCase}`);
        } else {
          pickRegion(region);
        }
      } else {
        pickRegion(somewhereElse);
        router.push(BASE_PATHS.HOMEOWNER);
      }
    };
  }

  return (
    <div
      className={clsx(
        styles.stateSelected,
        'gap-sm md:gap-lg grid auto-cols-min grid-flow-row-dense grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
        'my-xl md:my-lg md:border-gray md:pb-2xl md:border-0 md:border-b md:border-solid',
      )}
    >
      {withDescription && (
        <Text fontWeight="semibold" className={styles.stateSelected}>
          {template(REGION_SELECT_STRINGS.where_is_your_home_in, {
            state: name,
          })}
        </Text>
      )}
      {regionList
        .filter((region) =>
          from === 'listing' ? region.status === 'Active' : region,
        )
        .filter((region) =>
          search
            ? name.toLowerCase() === search ||
              region.region?.toLowerCase().includes(search)
            : true,
        )
        .map((region) => (
          <div className="flex flex-col items-center" key={region.id}>
            <RegionCard
              name={region.region}
              onClick={handleRegionClick(region)}
              status={region.status}
            />
          </div>
        ))}
    </div>
  );
}

export function StateCard({
  name,
  code,
  status,
  src,
  hoverSrc,
  selected,
  from,
  search,
  onClick,
  onDismiss,
}: StateCardProps) {
  const { isRegionsLoading } = useRegions();

  if (isRegionsLoading) {
    return null;
  }

  return (
    <>
      <ButtonBase onClick={onClick}>
        <div
          className={clsx(
            'duration-hover flex flex-col items-center rounded transition-colors',
            selected ? 'bg-navy' : 'bg-white',
          )}
        >
          <div
            className={clsx(
              styles.button,
              'relative flex flex-col justify-center rounded shadow',
            )}
          >
            <Image
              src={selected ? hoverSrc : src}
              alt=""
              className={clsx(styles.buttonImage, 'rounded')}
            />
            <div
              className={clsx('mt-xs', selected ? 'text-white' : 'text-navy')}
            >
              <Text variant="p1" fontWeight="semibold">
                {name}
              </Text>
            </div>
            {status === 'ComingSoon' && (
              <Text
                variant="p1"
                className={clsx(
                  'leading-p2',
                  selected ? 'text-white' : 'text-navy',
                )}
              >
                {STRINGS['card.comingSoon']}
              </Text>
            )}
          </div>
        </div>
      </ButtonBase>
      {selected && (
        <RegionList
          code={code}
          onDismiss={onDismiss}
          from={from}
          name={name}
          search={search}
        />
      )}
    </>
  );
}
